/**
 * ClaimOwnerSlideout — the CLAIM step of the ownership ceremony for a DISCOVERED_UNOWNED agent.
 *
 * Names the accountable owner (POST /ownership/agents/{id}/claim) → the record moves to NOMINATED,
 * and that owner then accepts (attest) to reach ACTIVE. The creator is NOT presumed to be the
 * owner; a human must explicitly nominate one (which may be themselves via "Nominate me").
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../../services/api';
import { nominateOwner } from '../../services/protectBuild';
import { useAuth } from '../../contexts/AuthContext';
import { Slideout, Button, Input, EmptyState, Skeleton, Pill } from '../../components/v2';
import { UserPlus, Search, ShieldCheck, HelpCircle } from 'lucide-react';

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

export default function ClaimOwnerSlideout({ finding, onClose, onClaimed }) {
  const { user, session } = useAuth();
  const myId = user?.id || session?.userId || null;
  const myName = user?.name || user?.email || 'Me';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [claimingId, setClaimingId] = useState(null);
  const [inviteMode, setInviteMode] = useState(false);
  const [invite, setInvite] = useState({ email: '', firstName: '', lastName: '' });
  const [inviting, setInviting] = useState(false);

  const open = !!finding;

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const body = unwrap(await api.getUsers({}));
        const list = Array.isArray(body) ? body : (Array.isArray(body?.content) ? body.content : []);
        if (alive) setUsers(list);
      } catch (e) {
        if (alive) setError(e?.message || 'Could not load users.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? users.filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q))
      : users;
    return rows.slice(0, 50);
  }, [users, search]);

  const claim = useCallback(async (ownerId, label) => {
    if (!finding?.agentId || !ownerId) return;
    setClaimingId(ownerId);
    setError(null);
    try {
      /* Through the shared service, not straight at the API. Protect's dashboard
         nominates an owner too, and two call sites reaching claimOwnership
         directly is two places for the ceremony to drift — one of them would
         gain the self-attest shortcut, or a different refusal message, and
         nobody would notice until a customer met both. */
      const res = await nominateOwner(finding.agentId, {
        ownerMode: 'nominate', ownerPrincipalId: ownerId,
      }, null);
      if (!res.ok) throw new Error(res.error);
      onClaimed?.(`${finding.agentName || 'Agent'} — nominated ${label} as accountable owner (now awaiting their acceptance).`);
    } catch (e) {
      setError(e?.status === 403
        ? 'You do not have permission to nominate an owner for this agent.'
        : (e?.message || 'Could not nominate the owner.'));
    } finally {
      setClaimingId(null);
    }
  }, [finding, onClaimed]);

  const onInvite = useCallback(async () => {
    if (!finding?.agentId) return;
    const email = (invite.email || '').trim();
    if (!email) { setError('Email is required to invite a new owner.'); return; }
    setInviting(true);
    setError(null);
    try {
      const res = await api.inviteOwnershipOwner({
        agentId: finding.agentId,
        email,
        firstName: invite.firstName.trim() || undefined,
        lastName: invite.lastName.trim() || undefined,
      });
      void res;
      onClaimed?.(`${finding.agentName || 'Agent'} — invited ${email} as accountable owner (invitation sent; awaiting their acceptance).`);
    } catch (e) {
      setError(e?.status === 403
        ? 'You do not have permission to invite an owner for this agent.'
        : (e?.message || 'Could not invite the owner.'));
    } finally {
      setInviting(false);
    }
  }, [finding, invite, onClaimed]);

  return (
    <Slideout
      isOpen={open}
      onClose={onClose}
      width={520}
      ariaLabel="Nominate accountable owner"
      header={(
        <div className="flex items-center gap-2">
          <UserPlus size={18} className="text-v2-trust" />
          <div>
            <div className="text-[14px] font-semibold text-v2-ink">Nominate accountable owner</div>
            <div className="text-[12px] text-v2-ink-muted">{finding?.agentName || 'Agent'}</div>
          </div>
        </div>
      )}
    >
      <div className="space-y-3">
        <div className="text-[12.5px] text-v2-ink-muted">
          Name the human who is accountable for this agent. They must then accept accountability
          (attest) for it to become active. The creator is provenance only — not the owner.
        </div>

        {myId ? (
          <Button variant="primary" size="sm" iconLeft={<ShieldCheck size={15} />}
            loading={claimingId === myId} onClick={() => claim(myId, 'yourself')}>
            Nominate me ({myName})
          </Button>
        ) : null}

        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-v2-ink-muted pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search people…"
            aria-label="Search people"
            className="w-full pl-9 pr-3 py-2 rounded-v2-md border border-v2-border bg-v2-surface text-v2-ink text-[13px] placeholder:text-v2-ink-muted focus:outline-none focus:border-v2-action focus:shadow-v2-xs hover:border-v2-border-strong"
          />
        </div>

        {error ? (
          <div className="text-[12.5px] text-v2-danger">{error}</div>
        ) : null}

        {loading ? (
          <Skeleton variant="text" lines={6} />
        ) : filtered.length === 0 ? (
          <EmptyState icon={<HelpCircle size={20} />} title="No people found" description="Try a different search." />
        ) : (
          <div className="divide-y divide-v2-border border border-v2-border rounded-v2-md">
            {filtered.map((u) => (
              <div key={u.id} className="flex items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-v2-ink truncate">{u.name || u.email || u.id}</div>
                  {u.email && u.name ? <div className="text-[11px] text-v2-ink-muted truncate">{u.email}</div> : null}
                </div>
                {u.id === myId ? <Pill tone="muted" size="sm">you</Pill> : null}
                <Button variant="secondary" size="sm" loading={claimingId === u.id}
                  onClick={() => claim(u.id, u.name || u.email || 'that person')}>
                  Nominate
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Invite a NEW person (business user) as the accountable owner — reuses the platform
            invite pipeline + the Ownership Assignment Invitation email. */}
        <div className="border-t border-v2-border pt-3">
          {!inviteMode ? (
            <button type="button" onClick={() => setInviteMode(true)}
              className="text-[12.5px] text-v2-action hover:underline inline-flex items-center gap-1">
              <UserPlus size={13} /> Not in the list? Invite a new person as owner
            </button>
          ) : (
            <div className="space-y-2">
              <div className="text-[12px] font-semibold text-v2-ink">Invite a new person</div>
              <input
                type="email"
                value={invite.email}
                onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                placeholder="Email (required)"
                aria-label="Invitee email"
                className="w-full px-3 py-2 rounded-v2-md border border-v2-border bg-v2-surface text-v2-ink text-[13px] placeholder:text-v2-ink-muted focus:outline-none focus:border-v2-action focus:shadow-v2-xs"
              />
              <div className="grid grid-cols-2 gap-2">
                <input value={invite.firstName} onChange={(e) => setInvite({ ...invite, firstName: e.target.value })}
                  placeholder="First name" aria-label="First name"
                  className="w-full px-3 py-2 rounded-v2-md border border-v2-border bg-v2-surface text-v2-ink text-[13px] placeholder:text-v2-ink-muted focus:outline-none focus:border-v2-action focus:shadow-v2-xs" />
                <input value={invite.lastName} onChange={(e) => setInvite({ ...invite, lastName: e.target.value })}
                  placeholder="Last name" aria-label="Last name"
                  className="w-full px-3 py-2 rounded-v2-md border border-v2-border bg-v2-surface text-v2-ink text-[13px] placeholder:text-v2-ink-muted focus:outline-none focus:border-v2-action focus:shadow-v2-xs" />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="primary" size="sm" loading={inviting} iconLeft={<UserPlus size={14} />} onClick={onInvite}>
                  Send invitation &amp; nominate
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setInviteMode(false)}>Cancel</Button>
              </div>
              <div className="text-[11px] text-v2-ink-muted">
                They receive the Ownership Assignment Invitation email, register, then accept accountability to activate the agent.
              </div>
            </div>
          )}
        </div>
      </div>
    </Slideout>
  );
}
